import React from 'react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { authFormBlur } from '../../../utils/auth-form-navigation';
import { LoginUserMobxDto } from '../../../models/mobx/dtos/auth/login-user-mobx-dto';
import { TextField } from '../../app/form/textfield/textfield';
import { AccountAgreementCheckbox } from '../dialogs/account/agree/account-agreement-checkbox';
import { FormItem } from '../../app/form/item/item';
import { WebButton as Button } from '../button/web-button';
import { PasswordField } from '../../app/form/textfield/password-field';

export interface LoginFormPropsInterface {
    onSubmit: (values: LoginUserMobxDto) => void;
    loading?: boolean;
}

export const LoginForm: React.FC<LoginFormPropsInterface> = observer((props) => {
    const { t } = useTranslation();
    const { onSubmit, loading } = props;

    const initialValues = new LoginUserMobxDto();
    const validationSchema = yup.object({
        email: yup
            .string()
            .required(t('layout.forms.common.emailValidationRequired'))
            .email(t('layout.forms.common.emailValidationValid')),
        password: yup.string().required(t('layout.forms.common.passwordValidation.required')),
        rememberMe: yup.boolean(),
    });

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
            {({ values, handleChange, touched, handleBlur, errors, handleSubmit }) => (
                <Form id={'login-form'}>
                    <FormItem>
                        <TextField
                            appearance="web"
                            margin="normal"
                            fullWidth
                            label={t('layout.forms.common.emailField')}
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={authFormBlur(handleBlur)}
                            helperText={touched.email && errors.email ? errors.email : ''}
                            error={Boolean(touched.email && errors.email)}
                        />
                    </FormItem>
                    <FormItem>
                        <PasswordField
                            appearance="web"
                            margin="normal"
                            fullWidth
                            name="password"
                            label={t('layout.forms.common.passwordField')}
                            value={values.password}
                            onChange={handleChange}
                            onBlur={authFormBlur(handleBlur)}
                            helperText={touched.password && errors.password ? errors.password : ''}
                            error={Boolean(touched.password && errors.password)}
                        />
                    </FormItem>
                    <FormItem>
                        <AccountAgreementCheckbox
                            name="rememberMe"
                            checked={values.rememberMe}
                            onChange={handleChange}
                            onBlur={authFormBlur(handleBlur)}
                            label={t('layout.forms.common.rememberCheckBox')}
                        />
                    </FormItem>
                    <FormItem>
                        <Button
                            size="large"
                            onClick={(e) => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                            variant="primary"
                            block
                            loading={loading}
                        >
                            {t('layout.forms.login.login')}
                        </Button>
                    </FormItem>
                </Form>
            )}
        </Formik>
    );
});
